import React from 'react';

export interface ConversationSummary {
  id: number;
  kind: 'general' | 'ticket' | string;
  ticket_key?: string | null;
  title: string;
  status: string;
  status_detail?: string | null;
  progress?: Record<string, string> | null;
  preview?: string | null;
  last_message_at?: string | null;
  branch_name?: string | null;
}

interface Props {
  conversations: ConversationSummary[];
  activeId: number | null;
  loading?: boolean;
  onSelect: (id: number) => void;
  onNewGeneral: () => void;
  onRefresh?: () => void;
}

const statusDot = (status: string) => {
  if (status === 'running') return 'bg-blue-500 animate-pulse';
  if (status === 'waiting_user') return 'bg-amber-500';
  if (status === 'queued' || status === 'pending') return 'bg-slate-400';
  if (status === 'failed') return 'bg-red-500';
  if (status === 'done') return 'bg-green-500';
  return 'bg-gray-400';
};

const ComplicConversationSidebar: React.FC<Props> = ({
  conversations,
  activeId,
  loading,
  onSelect,
  onNewGeneral,
  onRefresh,
}) => {
  return (
    <div className="w-52 flex-shrink-0 border-r border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/60 flex flex-col h-full min-w-0">
      <div className="p-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
        <button
          onClick={onNewGeneral}
          className="flex-1 text-xs font-semibold px-2 py-1.5 rounded-xl bg-complic-600 hover:bg-complic-700 text-white transition"
        >
          + Nový chat
        </button>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title="Obnovit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && conversations.length === 0 ? (
          <div className="p-4 text-xs text-gray-500 font-sans">Načítám…</div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-xs text-gray-500 font-sans">Zatím žádné konverzace</div>
        ) : (
          <ul className="py-1">
            {conversations.map((c) => {
              const active = c.id === activeId;
              const label = c.kind === 'ticket' && c.ticket_key
                ? c.ticket_key
                : (c.title || 'Chat');
              return (
                <li key={c.id}>
                  <button
                    onClick={() => onSelect(c.id)}
                    className={`w-full text-left px-3 py-2.5 border-l-2 transition ${
                      active
                        ? 'bg-white dark:bg-gray-800 border-blue-500'
                        : 'border-transparent hover:bg-white/70 dark:hover:bg-gray-800/70'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot(c.status)}`} />
                      <span className="text-sm font-sans font-semibold text-gray-800 dark:text-gray-100 truncate">
                        {c.kind === 'ticket' ? '🎫 ' : ''}{label}
                      </span>
                    </div>
                    {c.status_detail && (
                      <p className="mt-0.5 text-[10px] text-blue-600 dark:text-blue-400 font-sans truncate pl-3.5">
                        {c.status_detail}
                      </p>
                    )}
                    {c.preview && (
                      <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 font-sans truncate pl-3.5">
                        {c.preview}
                      </p>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ComplicConversationSidebar;
